/**
 * Core SDK Client
 */

import CryptoJS from 'crypto-js';
import { SDKConfig, getConfigFromEnv } from './config';
import { DomainEndpoint } from './endpoints/domain';
import { CustomerEndpoint } from './endpoints/customer';
import { ProductEndpoint } from './endpoints/product';
import { FinanceEndpoint } from './endpoints/finance';
import {
  DreamscapeException,
  AuthenticationException,
  BadRequestException,
  PaymentRequiredException,
  NotFoundException,
  InternalServerErrorException,
  InvalidResponseException,
} from './errors';


export class DreamscapeClient {
  public readonly domains: DomainEndpoint;
  public readonly customers: CustomerEndpoint;
  public readonly products: ProductEndpoint;
  public readonly finances: FinanceEndpoint;

  private config: SDKConfig;

  constructor(config?: Partial<SDKConfig>) {
    const envConfig = getConfigFromEnv();
    this.config = {
      ...envConfig,
      ...config,
    } as SDKConfig;

    if (!this.config.apiKey) {
      throw new Error('API key is required. Set VITE_DREAMSCAPE_API_KEY or provide it in config.');
    }

    // Initialize endpoints
    this.domains = new DomainEndpoint(this);
    this.customers = new CustomerEndpoint(this);
    this.products = new ProductEndpoint(this);
    this.finances = new FinanceEndpoint(this);
  }

  /**
   * Generate request ID (matches PHP's md5(uniqid() . microtime(true)))
   */
  private generateRequestId(): string {
    // PHP's md5(uniqid() . microtime(true))
    // uniqid() is roughly 13 hex chars based on microtime
    const now = Date.now();
    const microtime = now / 1000;
    const uniqid = Math.floor(now * 1000).toString(16).substring(0, 13);
    const combined = uniqid + microtime.toString() + Math.random().toString();
    return CryptoJS.MD5(combined).toString();
  }

  /**
   * Generate MD5 hash
   */
  private md5Hash(message: string): string {
    return CryptoJS.MD5(message).toString();
  }

  /**
   * Generate authentication headers
   */
  private getAuthHeaders(): { 'Api-Request-Id': string; 'Api-Signature': string } {
    // Research shows that for direct API calls, the server identifies the reseller
    // solely by the combination of a unique Request-Id and its matching Signature.
    // The Api-Key itself is NEVER sent in the clear.

    const requestId = this.generateRequestId();
    const signature = this.md5Hash(requestId + this.config.apiKey);

    return {
      'Api-Request-Id': requestId,
      'Api-Signature': signature,
    };
  }


  /**
   * Get base URL
   */
  getBaseUrl(): string {
    return this.config.baseUrl || 'https://reseller-api.ds.network';
  }

  /**
   * Check if dev mode is enabled
   */
  isDevMode(): boolean {
    return this.config.devMode === true;
  }

  /**
   * Check if payment verification should be skipped
   */
  shouldSkipPaymentVerification(): boolean {
    return this.config.skipPaymentVerification === true || this.isDevMode();
  }

  /**
   * Make HTTP request with retry logic
   */
  async request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    endpoint: string,
    data?: any,
    params?: Record<string, any>,
    retryCount: number = 0
  ): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = new URL(endpoint, baseUrl);
    const authHeaders = this.getAuthHeaders();

    // Add authentication and other request headers
    const headers: Record<string, string> = {
      'Accept': 'application/json'
    };

    const isBrowser = typeof window !== 'undefined';

    if (isBrowser) {
      // Set the allowed headers precisely as requested and allowed by CORS
      headers['Api-Request-Id'] = authHeaders['Api-Request-Id'];
      headers['Api-Signature'] = authHeaders['Api-Signature'];
    } else {
      // Use headers for non-browser requests
      Object.assign(headers, authHeaders);
    }

    // Add query parameters
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => url.searchParams.append(`${key}[]`, String(v)));
        } else if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const timeout = this.config.timeout || 60000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const options: RequestInit = {
      method,
      headers,
      signal: controller.signal,
      credentials: 'omit'
    };

    console.log(`📡 SDK Request [${method}]: ${url.toString()}`, {
      timeout: `${timeout}ms`,
      requestId: authHeaders['Api-Request-Id']
    });

    if (data && (method === 'POST' || method === 'PATCH')) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url.toString(), options);
      clearTimeout(timeoutId);

      // Handle dev mode payment bypass
      if (this.shouldSkipPaymentVerification() && response.status === 402) {
        // In dev mode, return a mock success response for payment errors
        console.warn('[DEV MODE] Payment verification skipped');
        return {} as T;
      }

      // Retry logic for 500/502 errors
      const maxRetries = this.config.maxRetries || 3;
      const retryDelay = this.config.retryDelay || 1000;

      if ((response.status === 500 || response.status === 502) && retryCount < maxRetries) {
        console.warn(
          `[API] Server error ${response.status} on ${endpoint}, retrying... (${retryCount + 1}/${maxRetries})`
        );

        // Exponential backoff: delay increases with each retry
        const delay = retryDelay * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));

        // Retry the request
        return this.request<T>(method, endpoint, data, params, retryCount + 1);
      }

      return this.handleResponse<T>(response, endpoint);
    } catch (error: any) {
      clearTimeout(timeoutId);

      // Handle timeout errors
      if (error.name === 'AbortError') {
        throw new InternalServerErrorException(
          `Request timeout after ${timeout}ms for ${endpoint}`
        );
      }

      // Retry network errors
      const maxRetries = this.config.maxRetries || 3;
      const retryDelay = this.config.retryDelay || 1000;

      if (retryCount < maxRetries && (error.name === 'TypeError' || error.name === 'NetworkError')) {
        console.warn(
          `[API] Network error on ${endpoint}, retrying... (${retryCount + 1}/${maxRetries})`
        );

        const delay = retryDelay * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));

        return this.request<T>(method, endpoint, data, params, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Handle HTTP response and convert to appropriate exception or data
   */
  private async handleResponse<T>(response: Response, endpoint?: string): Promise<T> {
    const text = await response.text();
    let data: any;

    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      // Log the raw response for debugging
      console.error(`[API] Failed to parse JSON response from ${endpoint || 'unknown endpoint'}:`, {
        status: response.status,
        statusText: response.statusText,
        responsePreview: text.substring(0, 500),
      });
      throw new InvalidResponseException(
        `Invalid JSON response from ${endpoint || 'server'}: ${text.substring(0, 200)}`
      );
    }

    // Handle error status codes
    if (response.status === 400) {
      const validationErrors = data.validation_errors || data.error_message;
      throw new BadRequestException(
        Array.isArray(validationErrors)
          ? validationErrors.join(', ')
          : validationErrors || 'Bad request',
        validationErrors
      );
    }

    if (response.status === 401) {
      const errorMessage = data.error_message || data.message || 'Authentication is required';

      // Check if this is an IP whitelist issue
      const isIpIssue = errorMessage.toLowerCase().includes('ip') ||
        errorMessage.toLowerCase().includes('whitelist') ||
        errorMessage.toLowerCase().includes('not allowed') ||
        errorMessage.toLowerCase().includes('access denied');

      if (isIpIssue) {
        throw new AuthenticationException(
          '⚠️ API Access Denied: Your server IP address is not configured for API access. Please whitelist your IP address in the Dreamscape API settings or contact support to enable API access from your server.'
        );
      }

      throw new AuthenticationException(errorMessage);
    }

    if (response.status === 402) {
      const paymentDetails: { totalPrice?: string; balance?: string } = {};
      const errorMessage = data.error_message || 'Payment is required';

      // Extract payment details from error message
      const priceMatch = errorMessage.match(/Total price:\s*([\d.]+)/i);
      const balanceMatch = errorMessage.match(/Balance:\s*([\d.]+)/i);
      if (priceMatch) paymentDetails.totalPrice = priceMatch[1];
      if (balanceMatch) paymentDetails.balance = balanceMatch[1];

      throw new PaymentRequiredException(errorMessage, paymentDetails);
    }

    if (response.status === 404) {
      // Extract resource type and ID from endpoint for better error messages
      let resourceType: string | undefined;
      let resourceId: string | number | undefined;

      if (endpoint) {
        // Check for common patterns like /messages/{id}, /message/{id}, etc.
        const messageMatch = endpoint.match(/\/(?:messages?|emails?)\/([^\/\?]+)/i);
        if (messageMatch) {
          resourceType = 'Message';
          resourceId = messageMatch[1];
        } else {
          // Try to extract resource type and ID from endpoint
          const parts = endpoint.split('/').filter(p => p);
          if (parts.length >= 2) {
            resourceType = parts[parts.length - 2].replace(/s$/, ''); // Remove plural 's'
            resourceId = parts[parts.length - 1];
          }
        }
      }

      const errorMessage = data.error_message || data.message || data.error;
      const defaultMessage = resourceType && resourceId
        ? `${resourceType} not found`
        : `Resource not found${endpoint ? ` at ${endpoint}` : ''}`;

      const notFoundException = new NotFoundException(
        errorMessage || defaultMessage,
        resourceType,
        resourceId
      );

      // Log for debugging
      console.warn(`[API] Resource not found:`, {
        endpoint,
        resourceType,
        resourceId,
        errorMessage,
      });

      throw notFoundException;
    }

    if (response.status === 500 || response.status === 502) {
      // Enhanced error message for 500 errors
      const errorMessage = data.error_message || data.message || data.error || 'Internal server error';
      const detailedMessage = endpoint
        ? `Server error (${response.status}) on ${endpoint}: ${errorMessage}`
        : `Server error (${response.status}): ${errorMessage}`;

      // Log detailed error information for debugging
      console.error(`[API] Server error details:`, {
        endpoint,
        status: response.status,
        errorMessage,
        responseData: data,
      });

      throw new InternalServerErrorException(detailedMessage);
    }

    if (!response.ok) {
      const errorMessage = data.error_message || data.message || data.error || `HTTP ${response.status}`;
      throw new DreamscapeException(
        endpoint
          ? `Error ${response.status} on ${endpoint}: ${errorMessage}`
          : errorMessage,
        response.status
      );
    }

    // Return data (handle different response formats)
    if (data.data !== undefined) {
      return data.data as T;
    }

    return data as T;
  }
}

// Export singleton instance creator
export function createClient(config?: Partial<SDKConfig>): DreamscapeClient {
  return new DreamscapeClient(config);
}

// Export default instance
export default createClient;

